import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  ButtonGroup,
  Card,
  Form,
  FormLayout,
  Frame,
  Badge,
  Layout,
  Link,
  Modal,
  Page,
  ResourceItem,
  ResourceList,
  TextField,
  Select,
  RangeSlider,
  TextStyle,
  TextContainer,
  Heading,
  Toast,
  Tooltip,
} from '@shopify/polaris';
import { CirclePlusMinor } from '@shopify/polaris-icons';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL, UPDATE_DEPARTMENT } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultDarkGrayColor, defaultGrayColor, hslaColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const DepartmentsSection = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);
  const [updateDepartment] = useMutation(UPDATE_DEPARTMENT);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;

  const [form, setForm] = useState({
    departments: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [preview, setPreview] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isDepartmentsModalOpen, setIsTestimonialModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [careerPortalTime, setCareerPortalTime] = useState();

  const toggleIsDepartmentsModalOpen = () => setIsTestimonialModalOpen(!isDepartmentsModalOpen);
  const toggleIsConfirmDeleteDepartmentModalOpen = () => setIsConfirmDeleteModalOpen(!isConfirmDeleteModalOpen);
  const togglePreviewModal = () => setPreview(!preview);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const departmentSection = propOr({}, 'department_section')(styleControls);
    const departments = propOr([], 'departments')(careerPortal);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    const departmentsSection = propOr(false, 'departmentsSection')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);
    setForm((prevForm) => ({
      ...prevForm,
      departments,
      ...styleControlsFormatted,
      departmentsSection,
      departmentSection: {
        ...prevForm.departmentSection,
        backgroundColor: departmentSection?.background_color || defaultWhiteColor,
        blockColor: departmentSection?.block_color || defaultWhiteColor,
        headingColor: departmentSection?.heading_color || defaultGrayColor,
        headingWeight: departmentSection?.heading_weight || '700',
        headingSize: departmentSection?.heading_size || '26',
        headingFontFamily: departmentSection?.heading_font_family || 'inherit',
        headingTextCasing: departmentSection?.heading_text_casing || 'uppercase',
        departmentColor: departmentSection?.department_color || defaultDarkGrayColor,
        subheadingFontFamily: departmentSection?.subheading_font_family || 'inherit',
        descriptionColor: departmentSection?.description_color || defaultDarkGrayColor,
        bodyFontFamily: departmentSection?.body_font_family || 'inherit',
        topSpacing: departmentSection?.top_spacing || '80',
        bottomSpacing: departmentSection?.bottom_spacing || '80',
      },
      departmentTitle: careerPortal?.departmentTitle || '',
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  const updateField = (fieldName) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      departmentSection: {
        ...form.departmentSection,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setTimeout(() => {
      setIsSaveBarActive(false);
      setShowSuccessBanner(true);
      setIsLoading(false);
    }, 1000);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setPreview(false);
      setConfirmModal(false);
      let promisesArray = [];

      if (!isEmpty(form.departments)) {
        promisesArray = form.departments.map(({ id, headingText, subheadingText, isHighlight }) => {
          return updateDepartment({
            variables: {
              departmentId: id,
              headingText,
              subheadingText,
              isHighlight: isHighlight,
              shopifyToken,
            },
          });
        });
      }

      setIsLoading(true);
      const careerPortal = propOr({}, 'careerPortal')(data);
      const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
      const formFiltered = { ...form };

      delete formFiltered.headingText;
      delete formFiltered.subheadingText;

      const payload = { variables: { ...careerPortal, ...formFiltered, shopifyToken, departmentsSection: publish } };

      delete payload.variables.styleControls;

      Promise.all([...promisesArray, save(payload)])
        .then(onSuccess)
        .catch(onError);
    };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Departments section'
          subtitle={filterPageTimeLabel('departments_section')}
          titleMetadata={
            <Badge status={form.departmentsSection ? 'success' : 'default'}>
              {form.departmentsSection ? 'Published' : 'Unpublished'}
            </Badge>
          }
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/benefits-section` : null,
            previousURL: !isSaveBarActive ? `/career-portal/diversity-statement-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                onClick={onSubmit(true)}
                disabled={!isSaveBarActive && form.departmentsSection}
                connectedDisclosure={
                  form.departmentsSection
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Departments section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Give job seekers an overview of your company’s structure with our departments section.
                    </p>
                  </>
                }>
                <Card sectioned>
                  <TextField
                    id='departmentTitle'
                    label='Title text'
                    value={form.departmentTitle}
                    name='departmentTitle'
                    onChange={updateField('departmentTitle')}
                  />
                  <br />
                  <TextStyle>Departments display in rows of 3</TextStyle>
                  <ResourceList
                    items={form.departments.filter(({ isHighlight }) => isHighlight)}
                    renderItem={(item) => {
                      const { id, headingText, subheadingText } = item;
                      return (
                        <ResourceItem
                          id={id}
                          shortcutActions={[
                            {
                              content: 'Edit',
                              onAction: () => {
                                setSelectedDepartment({
                                  id,
                                  headingText,
                                  subheadingText,
                                });
                                setForm({
                                  ...form,
                                  headingText: id,
                                  subheadingText,
                                });
                                toggleIsDepartmentsModalOpen();
                              },
                            },
                            {
                              content: 'Remove',
                              onAction: () => {
                                toggleIsConfirmDeleteDepartmentModalOpen();
                                setSelectedDepartment(item);
                              },
                            },
                          ]}>
                          <div style={{ padding: '10px 5px' }}>
                            <h3>
                              <TextStyle variation='strong'>{headingText}</TextStyle>
                            </h3>
                            <div>{subheadingText}</div>
                          </div>
                        </ResourceItem>
                      );
                    }}
                  />
                  {(form.departments || []).filter(({ isHighlight }) => isHighlight).length < 9 && (
                    <div className={'left-hand-footer'}>
                      <Button onClick={toggleIsDepartmentsModalOpen}>Add department</Button>
                    </div>
                  )}
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Layout'
                description='Customize the content alignment and spacing to match your store’s theme.'>
                <Card sectioned>
                  <FormLayout>
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Top spacing'
                        value={form.departmentSection?.topSpacing}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'topSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.departmentSection?.topSpacing}px`}
                      />
                      <RangeSlider
                        label='Bottom spacing'
                        value={form.departmentSection?.bottomSpacing}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'bottomSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.departmentSection?.bottomSpacing}px`}
                      />
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.departmentSection?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                    <ColorPicker
                      colorName='Department block'
                      defaultColor={form.departmentSection?.blockColor}
                      onSelectColor={updateColor('blockColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title color'
                      defaultColor={form.departmentSection?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.departmentSection?.headingSize}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.departmentSection?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'headingFontFamily' })}
                        value={form.departmentSection?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'headingWeight' })}
                        value={form.departmentSection?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'headingTextCasing' })}
                        value={form.departmentSection?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='DEPARTMENT NAME TEXT'>
                    <ColorPicker
                      colorName='Department name'
                      defaultColor={form.departmentSection?.departmentColor}
                      onSelectColor={updateColor('departmentColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Department name font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'subheadingFontFamily' })}
                        value={form.departmentSection?.subheadingFontFamily}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='DEPARTMENT DESCRIPTION TEXT'>
                    <ColorPicker
                      colorName='Department description'
                      defaultColor={form.departmentSection?.descriptionColor}
                      onSelectColor={updateColor('descriptionColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Department description font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'departmentSection', fieldName: 'bodyFontFamily' })}
                        value={form.departmentSection?.bodyFontFamily}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive && form.departmentsSection}>
                Publish
              </Button>
            </div>
          </Form>
        </Page>
        {toastMarkup}
      </Frame>
      <Modal
        open={isDepartmentsModalOpen}
        onClose={toggleIsDepartmentsModalOpen}
        title={selectedDepartment?.id ? 'Update department' : 'Add department'}
        primaryAction={{
          content: selectedDepartment?.id ? 'Update department' : 'Add department',
          onAction: () => {
            const deptIdx = (form.departments || []).findIndex(({ id }) => id === form.headingText);
            const updatedDepartment = {
              ...form.departments[deptIdx],
              subheadingText: form.subheadingText,
              isHighlight: true,
              shopifyToken,
            };

            let updatedDepartments = [...form.departments];
            updatedDepartments = updatedDepartments.map((department, index) => {
              if (index === deptIdx)
                return {
                  ...department,
                  ...updatedDepartment,
                };
              return department;
            });

            setForm({
              ...form,
              headingText: '',
              subheadingText: '',
              departments: updatedDepartments,
            });
            toggleIsDepartmentsModalOpen();
            setSelectedDepartment(null);
          },
          disabled: !form.headingText || !form.subheadingText,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setForm({
                ...form,
                headingText: '',
                subheadingText: '',
              });
              setSelectedDepartment(null);
              toggleIsDepartmentsModalOpen();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            {!!selectedDepartment?.id ? (
              <Select
                id='headingText'
                label='Department'
                name='headingText'
                options={[{ value: selectedDepartment.id, label: selectedDepartment.headingText }]}
                value={form.headingText}
              />
            ) : (
              <Select
                id='headingText'
                label='Department'
                name='headingText'
                options={form.departments
                  .filter(({ isHighlight }) => !isHighlight)
                  .map(({ id: value, headingText: label }) => ({ value, label }))}
                onChange={updateField('headingText')}
                placeholder={'Select...'}
                value={form.headingText}
              />
            )}
            <TextField
              id='subheadingText'
              label='Department description'
              value={form.subheadingText}
              name='subheadingText'
              multiline={5}
              onChange={updateField('subheadingText')}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal
        open={!!preview}
        large
        onClose={togglePreviewModal}
        primaryAction={{
          content: 'Publish',
          disabled: !isSaveBarActive && form.departmentsSection,
          onAction: onSubmit(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: togglePreviewModal,
          },
        ]}
        title={
          <TextContainer>
            <Heading>Preview department section</Heading>
            <p style={{ fontSize: '0.85em' }}>
              Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
              section will look on your store
            </p>
          </TextContainer>
        }
        loading={false}>
        <div className={'preview__modal'}>
          <section
            className='department_section'
            style={{
              backgroundColor: `${hslaColor(form.departmentSection?.backgroundColor, '#fff')}`,
            }}>
            <div className='wrapper'>
              <h2
                className='heading_text'
                style={{
                  color: `${hslaColor(form.departmentSection?.headingColor, '#3a3a3a')}`,
                  fontSize: `${form.departmentSection?.headingSize || '26'}px`,
                  fontWeight: `${form.departmentSection?.headingWeight || '700'}`,
                }}>
                {form.departmentTitle || 'Our departments'}
              </h2>
              <div className='department_grid'>
                {(form.departments || [])
                  .filter(({ isHighlight }) => isHighlight)
                  .map((item) => {
                    const { headingText, subheadingText } = { ...item };
                    return (
                      <div
                        className='department_item'
                        style={{
                          backgroundColor: `${hslaColor(form.departmentSection?.blockColor, '#fff')}`,
                        }}>
                        <h3
                          className='department_name'
                          style={{
                            color: `${hslaColor(form.departmentSection?.departmentColor, '#3a3a3a')}`,
                          }}>
                          {headingText}
                        </h3>
                        <p
                          className='department_description'
                          style={{
                            color: `${hslaColor(form.departmentSection?.descriptionColor, '#3a3a3a')}`,
                          }}>
                          {subheadingText}
                        </p>
                      </div>
                    );
                  })}
              </div>
            </div>
          </section>
        </div>
      </Modal>
      <Modal
        open={isConfirmDeleteModalOpen}
        onClose={toggleIsConfirmDeleteDepartmentModalOpen}
        title='Remove department'
        primaryAction={{
          content: 'Delete',
          onAction: () => {
            setForm({
              ...form,
              departments: form.departments.map((department) => {
                const { id } = { ...department };
                if (id === selectedDepartment.id) {
                  return {
                    ...department,
                    isHighlight: false,
                    subheadingText: '',
                  };
                }
                return department;
              }),
            });
            setSelectedDepartment(null);
            setIsSaveBarActive(true);
            toggleIsConfirmDeleteDepartmentModalOpen();
          },
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleIsConfirmDeleteDepartmentModalOpen,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>Are you sure you want to delete this department?</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={confirmModal}
        onClose={toggleConfirmModal}
        title={`${form.departmentsSection ? 'Unpublish' : 'Publish'} section`}
        primaryAction={{
          content: `Yes, ${form.departmentsSection ? 'unpublish' : 'publish'} section`,
          onAction: onSubmit(!form.departmentsSection),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleConfirmModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>{`Do you want to ${form.departmentsSection ? 'unpublish' : 'publish'} this section to your career portal page?`}</p>
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
};

export default DepartmentsSection;
